
import { useState, useCallback } from "react";
import { Panel } from "./use-panel-detection";

interface PanelReaderOptions {
  panels: Panel[];
  onNavigateToNextEpisode?: () => void;
  onNavigateToPrevEpisode?: () => void;
}

export const usePanelReader = ({ 
  panels, 
  onNavigateToNextEpisode, 
  onNavigateToPrevEpisode 
}: PanelReaderOptions) => {
  const [currentPanelIndex, setCurrentPanelIndex] = useState<number | null>(null);
  const [zoomMode, setZoomMode] = useState(false);

  const totalPanels = panels.length;

  const enterZoomMode = useCallback((panelIndex: number) => {
    setCurrentPanelIndex(panelIndex);
    setZoomMode(true);
  }, []);

  const exitZoomMode = useCallback(() => {
    setZoomMode(false);
  }, []);

  const goToNextPanel = useCallback(() => {
    if (currentPanelIndex === null) return;
    
    if (currentPanelIndex < totalPanels - 1) {
      setCurrentPanelIndex(currentPanelIndex + 1);
    } else if (onNavigateToNextEpisode) {
      // If we're at the last panel, go to the next episode
      onNavigateToNextEpisode();
      setZoomMode(false);
    }
  }, [currentPanelIndex, totalPanels, onNavigateToNextEpisode]);

  const goToPrevPanel = useCallback(() => {
    if (currentPanelIndex === null) return;
    
    if (currentPanelIndex > 0) {
      setCurrentPanelIndex(currentPanelIndex - 1);
    } else if (onNavigateToPrevEpisode) {
      // If we're at the first panel, go to the previous episode
      onNavigateToPrevEpisode();
      setZoomMode(false);
    }
  }, [currentPanelIndex, onNavigateToPrevEpisode]);

  return {
    currentPanelIndex,
    zoomMode,
    enterZoomMode,
    exitZoomMode,
    goToNextPanel,
    goToPrevPanel
  };
};
